import * as React from 'react';
import type { Promotion, PromotionFailure, PromotionRepo, PromotionTarget } from '@/types/api';
import {
  DEPLOY_STATUS,
  PROMOTION_FAILED_MEANING,
  PROMOTION_FAILED_RECOVERY,
  PROMOTION_MERGE_STATUS,
  PROMOTION_PREFLIGHT_STATUS,
  PROMOTION_STATUS,
  PROMOTION_TARGET,
} from '@/lib/copy';
import { formatClock, formatDuration, shortSha } from '@/lib/format';
import { useNow } from '@/lib/hooks';
import { cn } from '@/lib/utils';
import { StateChip } from '../state-chip';
import { CopyValue } from '../ui/copy-value';
import { LedgerNotches, LedgerRail, LedgerStop, type Notch } from './rail';
import type { GlyphKind } from './glyph';

/* ---------------------------------------------------------------------------
   A promotion, drawn on the SAME rail as a run.

   A promotion is a pre-flight followed by a sequence: for each repo in position
   order, re-read the target ref, merge with the pinned SHA, then trigger and
   VERIFY that repo's deploy before advancing to the next. It can die partway,
   and when it does some repos are merged and some are not.

   That is structurally the same thing the Run Ledger draws — a timed sequence
   of stops that can stop — so it gets the same geometry and the same glyphs. A
   reviewer who has learned to read one rail can read the other, and the failed
   stop is where the failure is explained, attached to the repository it
   actually happened to rather than floating above the whole promotion.

   Partial failure is the expected case here, not the exception. The layout is
   built for it: repos that merged stay visibly merged, the failed repo is named
   at its own stop, and nothing on this screen implies a retry exists.
--------------------------------------------------------------------------- */

/** The four markers of one repo's promotion. A real sequence, so the notches
    carry information rather than decorate the rail. */
const REPO_MARKERS = ['pre-flight', 'merge', 'deploy', 'verify'] as const;

function repoProgress(repo: PromotionRepo): { reached: number; failedAt: number | null } {
  if (repo.preflight_status === 'conflict') return { reached: 0, failedAt: 0 };
  if (repo.merge_status === 'failed') return { reached: 1, failedAt: 1 };

  const deploy = repo.deploy;
  if (repo.merge_status === 'pending') {
    return { reached: repo.preflight_status === 'clean' ? 1 : 0, failedAt: null };
  }
  if (repo.merge_status === 'skipped') return { reached: REPO_MARKERS.length, failedAt: null };

  // merged
  if (!deploy) return { reached: 2, failedAt: null };
  if (deploy.status === 'failed' || deploy.status === 'cancelled') return { reached: 3, failedAt: 3 };
  if (deploy.status === 'succeeded') return { reached: REPO_MARKERS.length, failedAt: null };
  if (deploy.status === 'verifying') return { reached: 3, failedAt: null };
  return { reached: 3, failedAt: null }; // queued or building
}

export function glyphForPromotionRepo(repo: PromotionRepo): GlyphKind {
  const { reached, failedAt } = repoProgress(repo);
  if (failedAt !== null) return 'failed';
  if (repo.merge_status === 'skipped') return 'skipped';
  if (reached >= REPO_MARKERS.length) return 'succeeded';
  if (repo.merge_status === 'merged' || repo.preflight_status === 'clean') return reached >= 1 ? 'running' : 'pending';
  return 'pending';
}

/** The one-line state of a repo, in the vocabulary the brief asks for:
    pending / pre-flight ok / merged / deployed / failed. */
export function promotionRepoState(repo: PromotionRepo): { label: string; tone: 'neutral' | 'active' | 'ok' | 'danger' } {
  if (repo.preflight_status === 'conflict') return { label: 'Conflict', tone: 'danger' };
  if (repo.merge_status === 'failed') return { label: 'Merge failed', tone: 'danger' };
  if (repo.merge_status === 'skipped') return { label: 'Skipped', tone: 'neutral' };
  if (repo.merge_status === 'merged') {
    const deploy = repo.deploy;
    if (!deploy) return { label: 'Merged', tone: 'active' };
    if (deploy.status === 'failed' || deploy.status === 'cancelled') return { label: 'Deploy failed', tone: 'danger' };
    if (deploy.status === 'succeeded') return { label: 'Deployed', tone: 'ok' };
    return { label: 'Deploying', tone: 'active' };
  }
  if (repo.preflight_status === 'clean') return { label: 'Pre-flight ok', tone: 'active' };
  return { label: 'Pending', tone: 'neutral' };
}

export function PromotionLedger({
  promotion,
  className,
  runbookHref,
}: {
  promotion: Promotion;
  className?: string;
  /** Fallback runbook, used when the failure carries none. */
  runbookHref?: string | null;
}) {
  const live = promotion.status === 'preflight' || promotion.status === 'merging' || promotion.status === 'deploying';
  const now = useNow(live);
  const target = PROMOTION_TARGET[promotion.target];
  const failure = promotion.failure ?? null;

  const repos = React.useMemo(
    () => [...promotion.repos].sort((a, b) => a.position - b.position),
    [promotion.repos],
  );

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
          <span className="text-sm font-medium text-ink">{target.verb}</span>
          <StateChip state={PROMOTION_STATUS[promotion.status]} />
          <span className="font-mono text-tiny text-ink-3">promotion {promotion.id}</span>
        </div>
        <span className="font-mono text-tiny tabular-nums text-ink-3" title={`Started ${formatClock(promotion.started_at)}`}>
          {formatDuration(promotion.started_at, promotion.finished_at, now)}
        </span>
      </div>

      {promotion.status === 'failed' ? <PromotionFailedNotice failure={failure} runbookHref={runbookHref} /> : null}

      <LedgerRail>
        {repos.map((repo) => (
          <PromotionRepoStop
            key={repo.repo_id}
            repo={repo}
            target={promotion.target}
            isFailure={failure?.repo_id === repo.repo_id}
            failure={failure?.repo_id === repo.repo_id ? failure : null}
          />
        ))}
      </LedgerRail>
    </div>
  );
}

function PromotionRepoStop({
  repo,
  target,
  isFailure,
  failure,
}: {
  repo: PromotionRepo;
  target: PromotionTarget;
  isFailure: boolean;
  failure: PromotionFailure | null;
}) {
  const { reached, failedAt } = repoProgress(repo);
  const state = promotionRepoState(repo);
  const glyph = glyphForPromotionRepo(repo);
  const deploy = repo.deploy;

  const notches: Notch[] = REPO_MARKERS.map((label, index) => ({
    key: label,
    label,
    state: failedAt === index ? 'current' : index < reached ? 'passed' : index === reached ? 'current' : 'future',
  }));

  return (
    <LedgerStop
      glyph={glyph}
      current={glyph === 'running'}
      title={
        <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="font-mono text-xs text-ink">{repo.repo_slug}</span>
          <span className="font-mono text-tiny text-ink-3">#{repo.position}</span>
          <StateChip size="sm" state={{ label: state.label, tone: state.tone }} />
        </span>
      }
      timing={repo.merge_commit_sha ? <CopyValue value={repo.merge_commit_sha} display={shortSha(repo.merge_commit_sha)} /> : undefined}
      subtitle={
        <span className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5">
          <span className="font-mono text-tiny text-ink-3">
            pre-flight {PROMOTION_PREFLIGHT_STATUS[repo.preflight_status].label.toLowerCase()}
          </span>
          <span aria-hidden className="text-ink-3">
            ·
          </span>
          <span className="font-mono text-tiny text-ink-3">
            merge {PROMOTION_MERGE_STATUS[repo.merge_status].label.toLowerCase()}
          </span>
          {deploy ? (
            <>
              <span aria-hidden className="text-ink-3">
                ·
              </span>
              <StateChip size="sm" state={DEPLOY_STATUS[deploy.status]} />
            </>
          ) : null}
        </span>
      }
    >
      <LedgerNotches notches={notches} />

      {deploy?.url && deploy.status === 'succeeded' ? (
        <a
          href={deploy.url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1.5 inline-block font-mono text-tiny text-action underline underline-offset-2 hover:text-action-hi"
        >
          {new URL(deploy.url).host}
        </a>
      ) : null}

      {repo.error_code || isFailure ? (
        <div className="mt-2 border-l-2 border-danger bg-danger-soft px-2.5 py-2">
          <p className="font-mono text-tiny uppercase tracking-[0.08em] text-danger">
            {repo.error_code ?? failure?.error_code ?? 'promotion_failed'}
            {failure?.stage ? ` · ${failure.stage}` : ''}
          </p>
          {repo.error_message || failure?.message ? (
            <p className="mt-1 break-words text-xs leading-[17px] text-ink">{repo.error_message ?? failure?.message}</p>
          ) : null}
          <p className="mt-1.5 text-xs leading-[17px] text-ink-2">
            The sequence stopped at <span className="font-mono">{repo.repo_slug}</span> while promoting to {target}. Repos
            above this stop are already merged and were not reverted.
          </p>
        </div>
      ) : null}
    </LedgerStop>
  );
}

/**
 * `promotion failed` — the one state in the whole product with no automated
 * recovery.
 *
 * It gets a full-width block rather than an error chip, because the single most
 * expensive thing a reviewer can do here is treat it as a transient failure and
 * press the button again. The block says what happened, names the exact scope of
 * the manual repair on both sides, and links the runbook. There is deliberately
 * no retry control anywhere near it.
 */
export function PromotionFailedNotice({
  failure,
  runbookHref,
  className,
}: {
  failure: PromotionFailure | null;
  runbookHref?: string | null;
  className?: string;
}) {
  const runbook = failure?.runbook_url ?? runbookHref ?? null;
  const merged = failure?.merged_repos ?? [];
  const unmerged = failure?.unmerged_repos ?? [];

  return (
    <section
      className={cn('border border-danger bg-danger-soft', className)}
      role="alert"
      aria-label="Promotion failed"
    >
      <div className="flex items-center gap-2 border-b border-danger/40 px-3 py-1.5">
        <svg aria-hidden viewBox="0 0 12 12" className="h-3 w-3 shrink-0 fill-danger">
          <path d="M6 0 12 11H0z" />
        </svg>
        <p className="font-mono text-tiny font-semibold uppercase tracking-[0.12em] text-danger">
          Promotion failed · no automated recovery
        </p>
      </div>

      <div className="space-y-2.5 px-3 py-2.5">
        <p className="max-w-prose text-xs leading-[17px] text-ink">{PROMOTION_FAILED_MEANING}</p>

        {failure ? (
          <div className="grid gap-2 sm:grid-cols-2">
            <RepoScope label="Merged — live on the target" repos={merged} tone="danger" />
            <RepoScope label="Not merged — still behind" repos={unmerged} tone="neutral" />
          </div>
        ) : null}

        {failure ? (
          <p className="font-mono text-tiny text-danger">
            failed at {failure.repo_slug ?? `repo ${failure.repo_id}`} · stage {failure.stage} · {failure.error_code}
          </p>
        ) : null}

        <p className="max-w-prose text-xs leading-[17px] text-ink-2">{PROMOTION_FAILED_RECOVERY}</p>

        {runbook ? (
          <a
            href={runbook}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 border border-danger px-2 py-1 font-mono text-tiny uppercase tracking-[0.08em] text-danger hover:bg-danger hover:text-danger-ink"
          >
            Open the operator runbook
            <svg aria-hidden viewBox="0 0 10 10" className="h-2.5 w-2.5 stroke-current" fill="none" strokeWidth="1.4">
              <path d="M3 1h6v6M9 1 1.5 8.5" strokeLinecap="round" />
            </svg>
          </a>
        ) : (
          <p className="font-mono text-tiny text-ink-3">
            No runbook URL was recorded on this failure. Page the operator directly.
          </p>
        )}
      </div>
    </section>
  );
}

function RepoScope({ label, repos, tone }: { label: string; repos: string[]; tone: 'danger' | 'neutral' }) {
  return (
    <div className="border border-rule bg-raised px-2 py-1.5">
      <p className="eyebrow">{label}</p>
      {repos.length === 0 ? (
        <p className="mt-1 font-mono text-tiny text-ink-3">none</p>
      ) : (
        <ul className="mt-1 m-0 list-none space-y-0.5 p-0">
          {repos.map((slug) => (
            <li key={slug} className={cn('font-mono text-xs', tone === 'danger' ? 'text-danger' : 'text-ink-2')}>
              {slug}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
